// Shared type transformations. Not to be confused with typings.d.ts

/**
 * Strip out non-public fields from a class.
 *
 * Useful when you want to say one class implements the same methods as another.
 * From https://github.com/microsoft/TypeScript/issues/471#issuecomment-381842426
 */
export type Interface<T> = {
  [P in keyof T]: T[P];
};

type UndefinedToNull<T> = T extends undefined ? T | null : T;

export type UndefinedFieldsToNull<T> = {
  [P in keyof T]-?: UndefinedFieldsToNull<UndefinedToNull<T[P]>>;
};

/**
 * Make only properties K of T optional,
 * unlike `Partial` which makes all of T's properties optional
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * This type encodes an optional value.
 *
 * It is most useful when `T` can be `undefined` or `null` and you wish to
 * distinguish between having a value of type `T` and not.
 */
export type Option<T> =
  | { readonly type: 'some'; readonly value: T }
  | { readonly type: 'none' };

export function isSome<T>(
  option: Option<T>,
): option is { readonly type: 'some'; readonly value: T } {
  return option.type === 'some';
}

export function isNone<T>(
  option: Option<T>,
): option is { readonly type: 'none' } {
  return option.type === 'none';
}

export function some<T>(value: T): Option<T> {
  return { type: 'some', value };
}

export function none<T = unknown>(): Option<T> {
  return { type: 'none' };
}

/**
 * This should rarely be used.
 * It is useful when saying the backend returns nothing.
 */
export type EmptyResponse = {} | null | [];
